import { DUPE_API_URL } from '@dupe/common/constants'
import type { OnboardingData } from '@dupe/data/enums'
import { useMutation } from '@tanstack/react-query'
import useAuthUser, { type AuthUserWithToken } from '~/src/hooks/useAuthUser'
import fetcher from '~/src/lib/fetcher'

interface MaybeRegisterResponse {
  user: {
    id: string | null
    tempUserIds: string[] | null
    isAnonymous: boolean | null
    metadata: Record<string, any> | null
    onboarding_data: OnboardingData | null
    createdAt: Date | null
  }
  existing: boolean
}

export const useMaybeRegister = () => {
  const { authUser } = useAuthUser()

  return useMutation({
    mutationFn: async (params?: {
      promoCode?: string
      /**
       * The freshly-authenticated Firebase user, for callers that have one in
       * hand. Overrides the hook's copy, and it has to, because of a race the
       * hook cannot win:
       *
       * `AuthProvider` calls this from INSIDE its own `onAuthStateChanged`
       * callback, while `useAuthUser` is a separate `react-firebase-hooks`
       * subscription. React has not re-rendered by then, so the `authUser`
       * captured in this closure is still `null` — and `fetcher` does not
       * complain about that, it just sends the request with no
       * `x-authorization` header at all. The API answers 401, no account is
       * created, and nothing surfaces the failure.
       *
       * Measured on a phone sign-up (2026-09-21, preview): the call went out at
       * +3.8s with no token and 401'd. On a brand-new account a later re-render
       * retried it successfully 7s afterwards; for a returning user the
       * `!userData?.id` gate meant it never retried at all.
       *
       * The caller's `user` is the same object Firebase just handed it, so its
       * token is valid immediately.
       */
      authUser?: AuthUserWithToken | null
    }) => {
      // `dataOrParams` becomes the POST body, so the override must not travel
      // with it — sending a whole Firebase user object as the body would be
      // both wrong and enormous.
      const { authUser: authUserOverride, ...body } = params ?? {}

      const data = await fetcher<MaybeRegisterResponse>(
        `${DUPE_API_URL}/api/user/maybe-register`,
        {
          method: 'POST',
          authUser: authUserOverride ?? authUser,
          dataOrParams: body,
        }
      )
      return data
    },
  })
}
